import { useEffect, useRef, useState } from "react";
import type { SimulationEntry } from "./useHiringData";

export const simEntryKey = (entry: SimulationEntry) => `${entry.role}-${entry.date.toISODate()}-${entry.nurseName}-${entry.shiftType}`;

/**
 * Tracks which simulation entries are "fresh" (just added) so they can be
 * animated on first appearance without re-animating on expand/collapse.
 *
 * Returns a Set of keys for entries that should fade in.
 * Once an entry has been seen, it won't appear in future sets.
 */
export const useSimulationAnimation = (data: SimulationEntry[]) => {
	const seenRef = useRef(new Set<string>());
	const [freshKeys, setFreshKeys] = useState(new Set<string>());

	useEffect(() => {
		if (data.length === 0) {
			seenRef.current = new Set();
			setFreshKeys(new Set());
			return;
		}

		const batch = new Set<string>();
		for (const entry of data) {
			const key = simEntryKey(entry);
			if (!seenRef.current.has(key)) {
				batch.add(key);
				seenRef.current.add(key);
			}
		}

		if (batch.size > 0) setFreshKeys(batch);
	}, [data]);

	return freshKeys;
};
